import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Plus, ArrowLeft } from "lucide-react";

export default function DimensionDetailPage() {
  const { id } = useParams();
  const [dim, setDim] = useState(null);
  const [values, setValues] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [dr, vr] = await Promise.all([
        api.get(`/tenant/dimensions`),
        api.get(`/tenant/dimensions/${id}/values`),
      ]);
      setDim(dr.data.items.find((d) => d.id === id) || null);
      setValues(vr.data.items);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [id]);

  const toggle = async (v) => {
    try {
      if (v.active) await api.delete(`/tenant/dimensions/${id}/values/${v.id}`);
      else await api.patch(`/tenant/dimensions/${id}/values/${v.id}`, { active: true });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="dimension-detail-page">
      <div>
        <Button asChild variant="ghost" size="sm"><Link to="/app/admin/dimensions"><ArrowLeft className="h-4 w-4" /> Dimensions</Link></Button>
        {dim && (
          <>
            <h1 className="mt-2 font-display text-3xl font-semibold text-primary">{dim.label}</h1>
            <p className="mt-2 flex items-center gap-2 text-muted-foreground">
              <Badge variant="secondary" className="rounded-full font-mono text-xs">{dim.key}</Badge>
              {dim.required && <Badge className="bg-warning/15 text-warning-foreground rounded-full">Required</Badge>}
            </p>
          </>
        )}
      </div>

      <div><NewValue dimId={id} onDone={load} /></div>

      <Card>
        <CardHeader><CardTitle>Values</CardTitle></CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Code</TableHead>
                <TableHead>Label</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && <TableRow><TableCell colSpan={4} className="py-8 text-center text-muted-foreground"><Loader2 className="mx-auto h-4 w-4 animate-spin" /></TableCell></TableRow>}
              {!loading && values.length === 0 && <TableRow><TableCell colSpan={4} className="py-8 text-center text-muted-foreground">No values yet.</TableCell></TableRow>}
              {values.map((v) => (
                <TableRow key={v.id}>
                  <TableCell><Badge variant="secondary" className="rounded-full font-mono">{v.code}</Badge></TableCell>
                  <TableCell className="font-medium">{v.label}</TableCell>
                  <TableCell><Badge variant={v.active ? "default" : "outline"} className="capitalize">{v.active ? "active" : "inactive"}</Badge></TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="outline" onClick={() => toggle(v)}>{v.active ? "Disable" : "Enable"}</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

function NewValue({ dimId, onDone }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ code: "", label: "" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/tenant/dimensions/${dimId}/values`, f);
      toast.success("Value added");
      setOpen(false); setF({ code: "", label: "" });
      onDone();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="new-value-btn"><Plus className="h-4 w-4" /> Add value</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Add value</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-2"><Label>Code</Label><Input value={f.code} onChange={(e) => setF({ ...f, code: e.target.value })} placeholder="J-100" data-testid="new-value-code" /></div>
          <div className="space-y-2"><Label>Label</Label><Input value={f.label} onChange={(e) => setF({ ...f, label: e.target.value })} placeholder="Roof repair" data-testid="new-value-label" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy || !f.code || !f.label} data-testid="new-value-save">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Add
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
